/**
 * LiveRecorder — Compact UI to record from mic, encode to MP3, and submit.
 * Props:
 *   label (string)     - Display label (e.g. "Enregistrer La voix de Ludovic")
 *   onRecorded(blob)   - Called with resulting Blob (audio/mp3) once stop is pressed
 *   disabled (bool)
 *   accent (string)    - Tailwind color stem, e.g. "amber" | "emerald"
 *   testIdPrefix       - data-testid prefix
 */
import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createMp3Recorder } from '../lib/mp3Recorder';

const ACCENT_CLASSES = {
  amber: 'bg-amber-500/15 border-amber-400/40 text-amber-200 hover:bg-amber-500/25',
  emerald: 'bg-emerald-500/15 border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/25',
};

export default function LiveRecorder({ label, onRecorded, disabled = false, accent = 'amber', testIdPrefix = 'live-rec' }) {
  const [recording, setRecording] = useState(false);
  const [encoding, setEncoding] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recRef.current && recording) {
      recRef.current.stop().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    if (disabled || recording || encoding) return;

    // 1) Check secure context (HTTPS required by browsers for getUserMedia)
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      toast.error("Le micro nécessite une connexion sécurisée (HTTPS).");
      return;
    }

    // 2) Check API availability
    if (!navigator?.mediaDevices?.getUserMedia) {
      toast.error("Votre navigateur ne supporte pas l'enregistrement audio. Essayez Chrome ou Firefox récent.");
      return;
    }

    try {
      const r = await createMp3Recorder();
      recRef.current = r;
      r.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      // Distinguish between specific error types
      const name = e?.name || '';
      const msg = e?.message || '';
      if (name === 'NotAllowedError' || /permission|denied/i.test(msg)) {
        toast.error("Permission micro refusée. Cliquez sur l'icône 🔒 dans la barre d'adresse et autorisez le micro pour ce site.", { duration: 8000 });
      } else if (name === 'NotFoundError' || /no.*device|not.*found/i.test(msg)) {
        toast.error("Aucun micro détecté. Connectez un micro ou un casque avec micro puis réessayez.");
      } else if (name === 'NotReadableError' || /busy|in use/i.test(msg)) {
        toast.error("Micro occupé par une autre application. Fermez Zoom/Meet/autre app utilisant le micro puis réessayez.");
      } else if (name === 'OverconstrainedError') {
        toast.error("Configuration micro non supportée. Essayez un autre micro.");
      } else if (name === 'SecurityError') {
        toast.error("Accès micro bloqué par les paramètres de sécurité du navigateur.");
      } else {
        // eslint-disable-next-line no-console
        console.error('[LiveRecorder] start error:', e);
        toast.error(`Erreur micro : ${name || 'inconnue'}. ${msg ? msg.slice(0, 80) : 'Vérifiez les permissions du navigateur.'}`, { duration: 8000 });
      }
    }
  };

  const stop = async () => {
    if (!recording || !recRef.current) return;
    setEncoding(true);
    if (timerRef.current) clearInterval(timerRef.current);
    try {
      const blob = await recRef.current.stop();
      recRef.current = null;
      setRecording(false);
      if (blob.size < 200) {
        toast.error('Enregistrement vide. Réessayez en parlant un peu plus longtemps.');
      } else {
        await onRecorded?.(blob);
      }
    } catch (e) {
      toast.error("Échec d'encodage MP3");
    } finally {
      setEncoding(false);
      setElapsed(0);
    }
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled || encoding}
      className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition disabled:opacity-50 ${
        recording
          ? 'bg-red-500/20 border-red-400/50 text-red-200 animate-pulse'
          : ACCENT_CLASSES[accent] || ACCENT_CLASSES.amber
      }`}
      data-testid={`${testIdPrefix}-toggle`}
      title={recording ? 'Arrêter et sauvegarder' : 'Démarrer l\'enregistrement micro'}
    >
      {encoding ? (
        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Encodage MP3…</>
      ) : recording ? (
        <><Square className="w-3.5 h-3.5" fill="currentColor" /> Stop · {elapsed}s</>
      ) : (
        <><Mic className="w-3.5 h-3.5" /> {label || 'Enregistrer ma voix'}</>
      )}
    </button>
  );
}
